import { Box, MenuItem, TextField, Typography } from "@mui/material";
import { DropdownIcon } from "app/icons";
import React, { UIEvent } from "react";

import { useStyles } from "./styles";
import {
  AddOrderDropDownKey,
  AddOrderForm,
  AddOrderSelectOption,
} from "../types";
import { OrderFieldConfig } from "./useAddOrderModal";

type Props = Omit<OrderFieldConfig, "key"> & {
  fieldKey: keyof AddOrderForm;
  value: string;
  fieldLabel: (label: string) => string;
  placeholder: (label: string, type: "select" | "text") => string;
  optionLabel: (option: AddOrderSelectOption) => string;
  onChange: (key: keyof AddOrderForm, value: string) => void;
  onDropDownScroll?: (dropDownKey?: AddOrderDropDownKey) => void;
  hasMoreDropDownItems?: (dropDownKey?: AddOrderDropDownKey) => boolean;
};

const AddOrderField = ({
  fieldKey,
  label,
  type,
  options = [],
  inputMode,
  disabled = false,
  dropDownKey,
  value,
  fieldLabel,
  placeholder,
  optionLabel,
  onChange,
  onDropDownScroll,
  hasMoreDropDownItems,
}: Props) => {
  const styles = useStyles();

  const handleMenuScroll = (event: UIEvent<HTMLElement>) => {
    if (!dropDownKey || !hasMoreDropDownItems?.(dropDownKey)) return;

    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    const remaining = scrollHeight - (scrollTop + clientHeight);

    if (remaining <= 2) {
      onDropDownScroll?.(dropDownKey);
    }
  };

  return (
    <Box className={styles.field}>
      <Typography className={styles.label}>{fieldLabel(label)}</Typography>
      <TextField
        className={styles.input}
        select={type === "select"}
        variant="outlined"
        disabled={disabled}
        placeholder={placeholder(label, type)}
        value={value}
        {...(inputMode ? { inputProps: { inputMode } } : {})}
        InputProps={{ notched: false }}
        {...(type === "select"
          ? {
              SelectProps: {
                displayEmpty: true,
                IconComponent: DropdownIcon,
                MenuProps: {
                  disableScrollLock: true,
                  PaperProps: {
                    className: styles.selectMenuPaper,
                    onScroll: handleMenuScroll,
                  },
                  MenuListProps: {
                    className: styles.selectMenuList,
                    onScroll: handleMenuScroll,
                  },
                },
                renderValue: (selectedValue) => {
                  if (!selectedValue) {
                    return (
                      <span className={styles.placeholder}>
                        {placeholder(label, "select")}
                      </span>
                    );
                  }

                  const selected = options.find(
                    (option) => option.value === selectedValue
                  );

                  return selected ? (
                    optionLabel(selected)
                  ) : (
                    <span className={styles.placeholder}>
                      {placeholder(label, "select")}
                    </span>
                  );
                },
              },
            }
          : {})}
        onChange={(event) => onChange(fieldKey, event.target.value)}
      >
        {type === "select" && [
          <MenuItem
            key="empty"
            value=""
            disabled
            className={styles.selectMenuItem}
          >
            {placeholder(label, "select")}
          </MenuItem>,
          ...options.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              className={styles.selectMenuItem}
            >
              {optionLabel(option)}
            </MenuItem>
          )),
        ]}
      </TextField>
    </Box>
  );
};

export default AddOrderField;
